import React, { useCallback, useMemo } from "react";
import { toArray } from "react-emoji-render";
import {
  MessageCenterConversationDetails,
  MessageCenterMessageDetails,
} from "./stores/messages/types";
import TailwindFlex from "library/components/_tailwind/flex";
// import MessagesCheckbox from "./messages-checkbox";
import Avatar from "library/components/avatar";
import TailwindText from "library/components/_tailwind/text";
import { inject, observer } from "mobx-react";
import ProfileStore from "../my-page/stores/profile/ProfileStore";
import TailwindIcon from "library/components/_tailwind/icon";
import MessageStore from "./stores/messages/MessageStore";
import sanitizeHtml from "sanitize-html";
import { TPseudoClasses } from "library/styles/tailwind-classnames";
import TailwindBox from "library/components/_tailwind/box";
import { formatTranslatedDate } from "library/core/utility";
import MessageCenterNavigatorCheckMark from "./message-navigator-checkmark";
import { WrappedComponentProps, injectIntl } from "react-intl";
import { CustomIconName } from "library/components/_tailwind/icon/icons/enums";
import {
  MC_CS_AGENT_PROFILE_IMG,
  isPendingVideoReviewPlaceholder,
} from "./stores/messages/consts";

type Props = {
  conversation: MessageCenterConversationDetails;
  readConversation: (conversation: MessageCenterConversationDetails) => void;
  selectConversation?: (conversation: MessageCenterConversationDetails) => void;
  onMultipleMatchesClick?: (
    conversation: MessageCenterConversationDetails
  ) => void;
  isActive?: boolean;
  isSelected?: boolean;
  showSelectionCheckBox?: boolean;
  profileStore?: ProfileStore;
  messageStore?: MessageStore;
  conversationsSearchTerm?: string;
  isMobileAndSearching?: boolean;
  canReceiveMessage: boolean;
  setCanCurrentMemberReceiveMsg: (canReceiveMsg: boolean) => void;
  isBulkMessageView?: boolean;
  userName?: string;
  showBorder?: boolean;
};

const MessageCenterConversation: React.ComponentType<
  Props & WrappedComponentProps
> = ({
  conversation,
  readConversation,
  selectConversation,
  onMultipleMatchesClick,
  isActive,
  isSelected,
  // showSelectionCheckBox = true,
  profileStore,
  messageStore,
  setCanCurrentMemberReceiveMsg,
  canReceiveMessage,
  conversationsSearchTerm,
  // isMobileAndSearching,
  userName,
  showBorder,
  intl,
}) => {
  const MSG_PREVIEW_CHAR_COUNT_BUFFER = 13;
  const { profile } = profileStore!;
  const { systemMessageUserIds, isCSAgentChatActive } = messageStore!;

  const searchedMessagesCount = useMemo(() => {
    const num_searched_messages = conversation?.searched_messages?.length || 0;

    return num_searched_messages;
  }, [conversation?.searched_messages]);

  const onClickConversation = useCallback(() => {
    if (searchedMessagesCount > 1) {
      onMultipleMatchesClick?.(conversation);
    } else {
      readConversation(conversation);
      setCanCurrentMemberReceiveMsg(canReceiveMessage);
    }
  }, [readConversation, searchedMessagesCount]);

  const toggleSelectedConversations = useCallback(() => {
    if (selectConversation) {
      selectConversation(conversation);
    }
  }, [selectConversation]);

  const formatMessagePreview = useCallback(
    (message: MessageCenterMessageDetails) => {
      let conversation_msg = isPendingVideoReviewPlaceholder(message)
        ? intl.formatMessage({
            id: "message-center.video-pending-review",
            defaultMessage: "Video message pending review",
          })
        : message?.preview || message?.data?.message || "";

      conversation_msg =
        conversation_msg && conversation_msg !== "null"
          ? conversation_msg
          : message?.data?.product && message?.data?.token_amount
          ? `${message?.data?.token_amount}`
          : intl.formatMessage({
              id: "message-center.attachments-fallback",
              defaultMessage: "Attachments",
            });

      if (conversationsSearchTerm && conversation_msg) {
        // find the part of the string where the keyword is visible
        const keyword_index = conversation_msg.indexOf(conversationsSearchTerm);
        if (keyword_index > MSG_PREVIEW_CHAR_COUNT_BUFFER) {
          conversation_msg = `...${conversation_msg.slice(
            keyword_index - MSG_PREVIEW_CHAR_COUNT_BUFFER
          )}`;
        }

        const content = toArray(conversation_msg);
        const new_msg = content.reduce((previous, current) => {
          if (
            typeof current === "string" &&
            conversationsSearchTerm &&
            current?.includes(conversationsSearchTerm)
          ) {
            const current_html = current.replace(
              `${conversationsSearchTerm}`,
              `<strong>${conversationsSearchTerm}</strong>`
            );
            return previous + current_html;
          } else if (typeof current === "string") {
            return previous + current;
          }
          return previous + (current?.["props"]?.children || "");
        }, "");
        conversation_msg = new_msg?.toString() || conversation_msg;
      }

      return `${sanitizeHtml(conversation_msg, {
        allowedTags: ["strong"],
      })}`;
    },
    []
  );

  const conversationParticipants = useMemo(
    () =>
      conversation?.participants.filter(
        participant => participant.username !== profile.username
      ),
    [conversation.participants]
  );

  const firstConversationParticipant = useMemo(
    () => conversationParticipants[0],
    [conversationParticipants]
  );

  const lastConversationMessage = useMemo(
    () => conversation?.last_message,
    [conversation?.last_message]
  );

  const lastConversationMessageDate = useMemo(
    () =>
      lastConversationMessage?.created_at
        ? formatTranslatedDate(
            intl,
            lastConversationMessage?.created_at,
            "MMM-D"
          )
        : "",
    [lastConversationMessage, intl]
  );

  const lastSearchedMessage = useMemo(() => {
    return searchedMessagesCount > 0 &&
      conversation?.searched_messages?.[searchedMessagesCount - 1].preview
      ? conversation?.searched_messages?.[searchedMessagesCount - 1]
      : "";
  }, [conversation?.searched_messages, searchedMessagesCount]);

  const lastSearchedMessageDate = useMemo(
    () =>
      lastSearchedMessage && lastSearchedMessage?.created_at
        ? formatTranslatedDate(intl, lastSearchedMessage?.created_at, "MMM-D")
        : "",
    [lastSearchedMessage, intl]
  );

  const conversationParticipantsNames = useMemo(
    () =>
      conversationParticipants
        .map(participant => participant.username)
        .join(", "),
    [conversationParticipants]
  );

  const classNameValues = useMemo(() => {
    if (!conversation.unread_count && !isActive)
      return [
        "bg-quaternary-bg-color",
        "hover:bg-highlight-color",
      ] as TPseudoClasses[];

    if (isActive) ["hover:bg-highlight-color"] as TPseudoClasses[];

    return ["hover:bg-highlight-color"] as TPseudoClasses[];
  }, [isActive, conversation]);

  const participantName = useMemo(() => {
    if (firstConversationParticipant?.username) {
      return firstConversationParticipant?.username;
    }
  }, [firstConversationParticipant]);

  const isAdmin = useMemo(() => {
    if (systemMessageUserIds.includes(conversationParticipants?.[0]?.id)) {
      return true;
    }

    return false;
  }, [conversationParticipants]);

  const showCount = useMemo(
    () => (conversation.unread_count > 0 ? conversation.unread_count : false),
    [lastConversationMessage, conversation.unread_count]
  );

  const isUserTheSender = useCallback(
    userId => {
      const foundParticipant = conversationParticipants?.find(
        participant => participant?.id === userId
      );

      return !foundParticipant; // user will not be found since it is filtered from messageStore
    },

    [conversationParticipants]
  );

  return (!conversationsSearchTerm &&
    lastConversationMessage &&
    !conversation.is_cs_convo) ||
    (conversationsSearchTerm &&
      lastSearchedMessage &&
      !conversation.is_cs_convo) ||
    (isCSAgentChatActive && conversation.is_cs_convo) ? (
    <TailwindFlex
      className={["MessageNavigatorConversation"]}
      backgroundColor={
        isActive || isSelected ? "bg-highlight-color" : "bg-quaternary-bg-color"
      }
      cursor={"cursor-pointer"}
      pseudoClasses={classNameValues}>
      <TailwindFlex
        borderWidth={showBorder ? ["border-t-2"] : []}
        borderColor={"border-primary-bg-color"}
        onClick={onClickConversation}
        height={"h-full"}
        style={{
          paddingRight: "15px",
        }}
        justifyContent='justify-center'
        alignItems='items-center'>
        {/* {showSelectionCheckBox && !isMobileAndSearching && (
        <TailwindFlex
          width={"w-auto"}
          alignItems={"items-center"}
          justifyContent={"justify-start"}
          padding={["pl-1"]}>
          <MessagesCheckbox
            isChecked={isSelected}
            onChange={toggleSelectedConversations}
            backgroundColor={"bg-white"}
          />
        </TailwindFlex>
      )} */}
        <TailwindFlex
          width={"w-auto"}
          alignItems={"items-center"}
          justifyContent={"justify-center"}
          position={"relative"}
          onClick={e => {
            e.stopPropagation();
            toggleSelectedConversations();
          }}
          style={{
            paddingLeft: "15px",
          }}>
          {isSelected && (
            <TailwindFlex
              position='absolute'
              zIndex='z-20'
              style={{
                top: "-2px",
                left: "0",
              }}>
              <MessageCenterNavigatorCheckMark />
            </TailwindFlex>
          )}
          {conversation.is_cs_convo ? (
            <Avatar
              photoURL={MC_CS_AGENT_PROFILE_IMG}
              username={intl.formatMessage({
                id: "liveChatSupport.select.modelSupport",
              })}
              size={"small"}
              randomBackgroundColor={false}
            />
          ) : (
            <Avatar
              photoURL={firstConversationParticipant?.profile_picture}
              username={firstConversationParticipant?.username}
              size={"small"}
              randomBackgroundColor={true}
            />
          )}
        </TailwindFlex>
        <TailwindFlex
          width={"w-auto"}
          flexGrow={"flex-grow"}
          flexShrink={"flex-shrink"}
          alignItems={"items-center"}
          overflow={["overflow-x-hidden"]}
          style={{
            paddingLeft: "15px",
          }}>
          <TailwindFlex
            width={"w-9/12"}
            flexDirection={"flex-col"}
            pseudoClasses={["md:gap-0.5"]}>
            <TailwindFlex padding={["pr-2"]}>
              <TailwindText
                style={{
                  fontSize: "14px",
                }}
                fontWeight={
                  conversation.unread_count ? "font-bold" : "font-normal"
                }
                textOverflow={"truncate"}>
                {conversation.is_cs_convo
                  ? intl.formatMessage({
                      id: "liveChatSupport.select.modelSupport",
                    })
                  : conversationParticipants.length === 1
                  ? participantName
                  : conversationParticipantsNames}
              </TailwindText>
              {isAdmin && (
                <TailwindText
                  verticalAlign={"align-middle"}
                  borderRadius={"rounded-sm"}
                  margin={["ml-2"]}
                  whiteSpace={"whitespace-nowrap"}
                  padding={["p-1"]}
                  backgroundColor={"bg-yellow-300"}
                  fontSize={"text-xs"}>
                  {intl.formatMessage({
                    id: "message-center.admin-badge",
                    defaultMessage: "Admin",
                  })}
                </TailwindText>
              )}
            </TailwindFlex>

            <TailwindFlex
              className={["MessageNavigatorConversationPreview"]}
              maxWidth={"max-w-xs"}
              pseudoClasses={["md:max-w-lg"]}
              textColor={"text-secondary-color"}>
              {conversationsSearchTerm && searchedMessagesCount > 1 ? (
                <span>
                  {intl.formatMessage(
                    {
                      id: "message-center.number-of-matched-messages",
                      defaultMessage: "{count} matched messages",
                    },
                    {
                      count: searchedMessagesCount,
                    }
                  )}
                </span>
              ) : conversationsSearchTerm && lastSearchedMessage ? (
                <>
                  <span>
                    {`${
                      lastSearchedMessage?.sender?.username === userName ||
                      isUserTheSender(lastSearchedMessage?.sender)
                        ? `${intl.formatMessage({
                            id: "verbiage.you",
                            defaultMessage: "You",
                          })}: `
                        : ""
                    }`}
                  </span>

                  {lastSearchedMessage?.data?.product === "tip_bio" &&
                  lastSearchedMessage?.data?.token_amount ? (
                    <TailwindIcon name={CustomIconName.token} />
                  ) : lastSearchedMessage?.data?.product &&
                    lastSearchedMessage?.data?.token_amount ? (
                    <TailwindIcon name={CustomIconName.gift2} />
                  ) : null}

                  <span
                    dangerouslySetInnerHTML={{
                      __html: formatMessagePreview(lastSearchedMessage),
                    }}></span>
                </>
              ) : lastConversationMessage ? (
                <>
                  {lastConversationMessage?.sender?.username === userName ||
                    (isUserTheSender(lastConversationMessage?.sender) && (
                      <span>
                        {`${
                          lastConversationMessage?.sender?.username ===
                            userName ||
                          isUserTheSender(lastConversationMessage?.sender)
                            ? `${intl.formatMessage({
                                id: "verbiage.you",
                                defaultMessage: "You",
                              })}: `
                            : ""
                        }`}
                        &nbsp;
                      </span>
                    ))}

                  {lastConversationMessage?.data?.product === "tip_bio" &&
                  lastConversationMessage?.data?.token_amount ? (
                    <span>
                      <TailwindIcon name={CustomIconName.token} />
                    </span>
                  ) : lastConversationMessage?.data?.product &&
                    lastConversationMessage?.data?.token_amount ? (
                    <span>
                      <TailwindIcon name={CustomIconName.gift2} />
                    </span>
                  ) : null}

                  <span
                    dangerouslySetInnerHTML={{
                      __html: formatMessagePreview(lastConversationMessage),
                    }}></span>
                </>
              ) : null}
            </TailwindFlex>
          </TailwindFlex>

          <TailwindFlex
            width={"w-3/12"}
            flexDirection={"flex-col"}
            alignItems={"items-end"}
            justifyContent={"justify-end"}>
            <TailwindText
              width={"w-full"}
              fontSize={"text-xs"}
              textColor={"text-gray-500"}
              textAlign={"text-right"}>
              {conversationsSearchTerm && lastSearchedMessage
                ? lastSearchedMessageDate
                : lastConversationMessageDate}
            </TailwindText>
            {showCount ? (
              <TailwindBox
                style={{
                  background: "#e9a23b",
                  color: "white",
                  fontStyle: "normal",
                  fontSize: 12,
                }}
                margin={["mt-1"]}
                fontWeight='font-bold'
                textAlign='text-center'
                borderRadius={"rounded-xl"}
                padding={["px-2", "py-0.5"]}>
                {showCount}
              </TailwindBox>
            ) : (
              <TailwindFlex>&nbsp;</TailwindFlex>
            )}
          </TailwindFlex>
        </TailwindFlex>
      </TailwindFlex>
    </TailwindFlex>
  ) : null;
};
export default injectIntl(
  inject("profileStore", "messageStore")(observer(MessageCenterConversation))
);
